import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "./components/ui/sonner";
import axios from "axios";

// Pages
import ClosedPage from "./pages/ClosedPage";
import HomePage from "./pages/HomePage";
import DestinationsPage from "./pages/DestinationsPage";
import CruiseDetailPage from "./pages/CruiseDetailPage";
import ReservationPage from "./pages/ReservationPage";
import ContactPage from "./pages/ContactPage";
import ClubPage from "./pages/ClubPage";
import SlowTourismePage from "./pages/SlowTourismePage";
import NaviresPage from "./pages/NaviresPage";
import VesselDetailPage from "./pages/VesselDetailPage";
import CGVPage from "./pages/CGVPage";
import PolitiqueConfidentialitePage from "./pages/PolitiqueConfidentialitePage";
import NotFoundPage from "./pages/NotFoundPage";

// Admin Pages
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminCruises from "./pages/admin/AdminCruises";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminQuotes from "./pages/admin/AdminQuotes";
import AdminMessages from "./pages/admin/AdminMessages";
import AdminPages from "./pages/admin/AdminPages";
import AdminPlanning from "./pages/admin/AdminPlanning";
import AdminFinancier from "./pages/admin/AdminFinancier";
import AdminDevis from "./pages/admin/AdminDevis";
import AdminCommandes from "./pages/admin/AdminCommandes";
import AdminLastMinute from "./pages/admin/AdminLastMinute";
import CommandePaymentPage from "./pages/CommandePaymentPage";
import AdminOffres from "./pages/admin/AdminOffres";
import AdminAgences from "./pages/admin/AdminAgences";
import DevisSignPage from "./pages/DevisSignPage";
import AgencyDevisSignPage from "./pages/AgencyDevisSignPage";
import ContractSignPage from "./pages/ContractSignPage";
import OwnerContractSignPage from "./pages/OwnerContractSignPage";
import OwnerPortal from "./pages/OwnerPortal";
import AgencyPortal from "./pages/AgencyPortal";
import AdminOwnerContracts from "./pages/admin/AdminOwnerContracts";
import AdminClients from "./pages/admin/AdminClients";

// Context
import { AuthProvider } from "./context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    // If a hash is present, scroll to the target element instead of forcing top
    if (hash) {
      const id = hash.replace('#', '');
      // Give React a tick to render the destination page
      const tryScroll = (attempt = 0) => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (attempt < 10) {
          setTimeout(() => tryScroll(attempt + 1), 100);
        }
      };
      tryScroll();
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, [pathname, hash]);
  return null;
}

function App() {
  useEffect(() => {
    // Remove platform badge
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) badge.remove();
    };
    removeBadge();
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    const interval = setInterval(removeBadge, 300);
    return () => { observer.disconnect(); clearInterval(interval); };
  }, []);

  useEffect(() => {
    // Seed initial data on first load
    const seedData = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (e) {
        // Data may already be seeded, ignore
      }
    };
    seedData();
  }, []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          {/* Site fermé — landing page */}
          <Route path="/" element={<ClosedPage />} />
          <Route path="/croisieres-catamaran-corse-sognudimare" element={<ClosedPage />} />
          <Route path="/destinations-croisieres-catamaran" element={<ClosedPage />} />
          <Route path="/slow-tourisme-croisieres-responsables" element={<ClosedPage />} />
          <Route path="/les-navires-disponibles" element={<ClosedPage />} />
          <Route path="/lagoon-38-sognudimare" element={<ClosedPage />} />
          <Route path="/lagoon-43-sognudimare" element={<ClosedPage />} />
          <Route path="/lagoon-46-sognudimare" element={<ClosedPage />} />
          <Route path="/sunreef-50-sognudimare" element={<ClosedPage />} />
          <Route path="/tanna-47-sognudimare" element={<ClosedPage />} />
          <Route path="/tour-de-corse-croisiere-catamaran" element={<ClosedPage />} />
          <Route path="/croisiere-catamaran-corse-du-sud" element={<ClosedPage />} />
          <Route path="/croisiere-catamaran-ouest-corse" element={<ClosedPage />} />
          <Route path="/croisiere-catamaran-sardaigne-corse" element={<ClosedPage />} />
          <Route path="/croisiere-catamaran-grece-authentique" element={<ClosedPage />} />
          <Route path="/croisiere-catamaran-grece-cyclades-2-semaines" element={<ClosedPage />} />
          <Route path="/croisiere-catamaran-grenadines-caraibes" element={<ClosedPage />} />
          <Route path="/reservation-cabines-privatisation-catamaran-sognudimare" element={<ClosedPage />} />
          <Route path="/contact" element={<ClosedPage />} />
          <Route path="/carte-club-des-voyageurs" element={<ClosedPage />} />
          <Route path="/club" element={<ClosedPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialitePage />} />

          {/* Legacy public routes kept (devis/contrats clients déjà engagés) */}
          <Route path="/location-catamaran-avec-equipage-skipper-corse" element={<Navigate to="/" replace />} />
          <Route path="/destinations-croisières-catamaran" element={<Navigate to="/destinations-croisieres-catamaran" replace />} />
          <Route path="/réservation-cabines-privatisation-catamaran-sognudimare" element={<Navigate to="/reservation-cabines-privatisation-catamaran-sognudimare" replace />} />
          <Route path="/croisiere-catamaran-sardaigne-et-corse-du-sud" element={<Navigate to="/croisiere-catamaran-sardaigne-corse" replace />} />
          <Route path="/croisieres-catamaran-corse" element={<Navigate to="/destinations-croisieres-catamaran" replace />} />
          <Route path="/booking-page" element={<Navigate to="/reservation-cabines-privatisation-catamaran-sognudimare" replace />} />
          <Route path="/carte-club-voyageurs" element={<Navigate to="/carte-club-des-voyageurs" replace />} />
          
          {/* Public Devis Signature Page */}
          <Route path="/devis/sign/:signToken" element={<DevisSignPage />} />
          
          {/* Agency Devis Public Sign Page */}
          <Route path="/agence/devis/:signToken" element={<AgencyDevisSignPage />} />
          
          {/* Partnership Contract Public Sign Page */}
          <Route path="/contrat/:signToken" element={<ContractSignPage />} />
          
          {/* Owner Contract Public Sign Page */}
          <Route path="/contrat-proprietaire/:signToken" element={<OwnerContractSignPage />} />
          
          {/* Owner Portal */}
          <Route path="/proprietaire" element={<OwnerPortal />} />
          <Route path="/proprietaire/*" element={<OwnerPortal />} />
          
          {/* Agency Portal */}
          <Route path="/agence" element={<AgencyPortal />} />
          <Route path="/agence/*" element={<AgencyPortal />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/cruises" element={<AdminCruises />} />
          <Route path="/admin/planning" element={<AdminPlanning />} />
          <Route path="/admin/financier" element={<AdminFinancier />} />
          <Route path="/admin/bookings" element={<AdminBookings />} />
          <Route path="/admin/quotes" element={<AdminQuotes />} />
          <Route path="/admin/devis" element={<AdminDevis />} />
          <Route path="/admin/commandes" element={<AdminCommandes />} />
          <Route path="/admin/last-minute" element={<AdminLastMinute />} />
          <Route path="/commande/pay/:commandeId/:installmentIdx" element={<CommandePaymentPage />} />
          <Route path="/admin/offres" element={<AdminOffres />} />
          <Route path="/admin/agences" element={<AdminAgences />} />
          <Route path="/admin/messages" element={<AdminMessages />} />
          <Route path="/admin/pages" element={<AdminPages />} />
          <Route path="/admin/owner-contracts" element={<AdminOwnerContracts />} />
          <Route path="/admin/clients" element={<AdminClients />} />

          {/* 404 catch-all */}
          <Route path="*" element={<ClosedPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
